import { useCallback, useEffect, useState } from 'react';
import styles from './Affiliates.module.css';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import Modal, { ModalDivider, ModalTitle, DetailRow } from '../../components/Modal/Modal';
import Pagination from '../../components/Pagination/Pagination';
import FilterSelect from '../../components/FilterSelect/FilterSelect';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import Avatar from '../../components/Avatar/Avatar';
import { api, cap, money, shortDate } from '../../lib/api';

// The affiliate programme: approve applications (which issues the code),
// record MoMo payouts for qualified referrals, reject referrals that look
// fake, and tune the terms. The rules live in the API's utils/affiliates.js —
// this page only drives them.

const STATUS_PARAM = { Applied: 'applied', Approved: 'approved', Suspended: 'suspended', Rejected: 'rejected' };

// StatusBadge only knows a few colours, so map onto those.
const BADGE_VARIANT = { applied: 'Pending', approved: 'Active', suspended: 'Inactive', rejected: 'muted' };
const REFERRAL_VARIANT = { pending: 'muted', qualified: 'Pending', paid: 'Active', rejected: 'Inactive' };

const EMPTY_TERMS = { enabled: true, bounty: '20', minOrderTotal: '50', holdDays: '7', claimWindowDays: '7' };

const Affiliates = () => {
    const [affiliates, setAffiliates] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [page, setPage] = useState(1);
    const [terms, setTerms] = useState(null);
    const [termsForm, setTermsForm] = useState(EMPTY_TERMS);
    const [termsOpen, setTermsOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [approveFor, setApproveFor] = useState(null);
    const [customCode, setCustomCode] = useState('');
    const [payoutFor, setPayoutFor] = useState(null);
    const [reference, setReference] = useState('');
    const [busy, setBusy] = useState(false);

    const loadAffiliates = useCallback(async () => {
        const params = new URLSearchParams({ page: String(page), limit: '20' });
        if (search.trim()) params.set('search', search.trim());
        if (STATUS_PARAM[statusFilter]) params.set('status', STATUS_PARAM[statusFilter]);
        try {
            const data = await api(`/admin/affiliates?${params.toString()}`);
            setAffiliates(data.affiliates);
            setTotalPages(Math.max(1, data.pages));
        } catch {
            setAffiliates([]);
        }
    }, [search, statusFilter, page]);

    const loadTerms = useCallback(async () => {
        try {
            const { settings } = await api('/admin/settings');
            setTerms(settings.affiliate);
        } catch {
            setTerms(null);
        }
    }, []);

    useEffect(() => {
        loadAffiliates();
    }, [loadAffiliates]);

    useEffect(() => {
        loadTerms();
    }, [loadTerms]);

    const openDetail = async (affiliate) => {
        try {
            setDetail(await api(`/admin/affiliates/${affiliate.id}`));
        } catch (err) {
            alert(err.message);
        }
    };

    // Run an admin action, then refresh whatever is on screen.
    const act = async (work) => {
        setBusy(true);
        try {
            await work();
            await loadAffiliates();
            if (detail) setDetail(await api(`/admin/affiliates/${detail.affiliate.id}`));
            return true;
        } catch (err) {
            alert(err.message);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const post = (path, body) => api(path, { method: 'POST', body });

    const approve = async () => {
        const ok = await act(() =>
            post(`/admin/affiliates/${approveFor.id}/approve`, customCode.trim() ? { code: customCode.trim() } : {})
        );
        if (ok) setApproveFor(null);
    };

    const reject = (a) => {
        const note = window.prompt(`Reason for rejecting ${a.name || 'this application'} (shown to them):`, '');
        if (note === null) return;
        act(() => post(`/admin/affiliates/${a.id}/reject`, { note }));
    };

    const suspend = (a) => {
        const note = window.prompt(`Why is ${a.name || 'this affiliate'} being suspended? Their code stops working.`, '');
        if (note === null) return;
        act(() => post(`/admin/affiliates/${a.id}/suspend`, { note }));
    };

    const reinstate = (a) => act(() => post(`/admin/affiliates/${a.id}/reinstate`));

    const rejectReferral = (referral) => {
        const reason = window.prompt('Why is this referral not being counted? (e.g. fake account)', '');
        if (!reason) return;
        act(() => post(`/admin/referrals/${referral.id}/reject`, { reason }));
    };

    const recordPayout = async () => {
        const ok = await act(() => post(`/admin/affiliates/${payoutFor.id}/payouts`, { reference: reference.trim() }));
        if (ok) setPayoutFor(null);
    };

    const openTerms = () => {
        setTermsForm({
            enabled: terms.enabled,
            bounty: String(terms.bounty),
            minOrderTotal: String(terms.minOrderTotal),
            holdDays: String(terms.holdDays),
            claimWindowDays: String(terms.claimWindowDays),
        });
        setTermsOpen(true);
    };

    const termsValid = ['bounty', 'minOrderTotal', 'holdDays', 'claimWindowDays'].every(
        (k) => termsForm[k] !== '' && Number.isInteger(Number(termsForm[k])) && Number(termsForm[k]) >= 0
    );

    const saveTerms = async () => {
        try {
            const { settings } = await api('/admin/settings', {
                method: 'PATCH',
                body: {
                    affiliate: {
                        enabled: termsForm.enabled,
                        bounty: Number(termsForm.bounty),
                        minOrderTotal: Number(termsForm.minOrderTotal),
                        holdDays: Number(termsForm.holdDays),
                        claimWindowDays: Number(termsForm.claimWindowDays),
                    },
                },
            });
            setTerms(settings.affiliate);
            setTermsOpen(false);
        } catch (err) {
            alert(err.message);
        }
    };

    const menuItems = (a) => {
        const status = a.affiliate.status;
        const items = [{ label: 'View affiliate', onClick: () => openDetail(a) }];
        if (status === 'applied' || status === 'rejected') {
            items.push({
                label: 'Approve',
                onClick: () => {
                    setCustomCode('');
                    setApproveFor(a);
                },
            });
        }
        if (status === 'applied') items.push({ label: 'Reject application', danger: true, onClick: () => reject(a) });
        if (status === 'approved' && a.due.count > 0) {
            items.push({
                label: `Record payout (${money(a.due.kwacha)})`,
                onClick: () => {
                    setReference('');
                    setPayoutFor(a);
                },
            });
        }
        if (status === 'approved') items.push({ label: 'Suspend', danger: true, onClick: () => suspend(a) });
        if (status === 'suspended') items.push({ label: 'Reinstate', onClick: () => reinstate(a) });
        return items;
    };

    return (
        <>
            <div className={`${styles.card} p-4 mb-4`}>
                <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                    <div>
                        <h2 className={`${styles.cardTitle} mb-2`}>Programme terms</h2>
                        {terms ? (
                            <p className={`${styles.terms} mb-0`}>
                                <strong>{money(terms.bounty)}</strong> per referred user who completes an order of{' '}
                                <strong>{money(terms.minOrderTotal)}</strong>+ or buys points · paid after a{' '}
                                <strong>{terms.holdDays}-day</strong> hold · codes accepted up to{' '}
                                <strong>{terms.claimWindowDays} days</strong> after signup
                            </p>
                        ) : (
                            <p className={`${styles.terms} mb-0`}>Loading…</p>
                        )}
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        {terms && (
                            <StatusBadge
                                status={terms.enabled ? 'Active' : 'Paused'}
                                variant={terms.enabled ? 'Active' : 'muted'}
                            />
                        )}
                        <Button variant="outline" disabled={!terms} onClick={openTerms}>
                            Edit terms
                        </Button>
                    </div>
                </div>
            </div>

            <div className={`${styles.card} p-4`}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h2 className={`${styles.cardTitle} mb-0`}>Affiliates</h2>
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <FilterSelect
                            label="Status"
                            value={statusFilter}
                            options={['All', 'Applied', 'Approved', 'Suspended', 'Rejected']}
                            onChange={(value) => {
                                setStatusFilter(value);
                                setPage(1);
                            }}
                        />
                        <SearchInput
                            placeholder="Name, phone or code..."
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                                setPage(1);
                            }}
                        />
                    </div>
                </div>

                <div className={`${styles.tableWrap} table-responsive mt-3`}>
                    <table className={`${styles.table} w-100`}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Code</th>
                                <th>MoMo number</th>
                                <th>Referrals</th>
                                <th>Paid out</th>
                                <th>Due now</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {affiliates.length === 0 && (
                                <tr>
                                    <td colSpan={8} className={styles.sub}>
                                        No affiliates{statusFilter !== 'All' ? ` with status ${statusFilter.toLowerCase()}` : ''}.
                                    </td>
                                </tr>
                            )}
                            {affiliates.map((a, index) => (
                                <tr key={a.id}>
                                    <td>
                                        <div className="d-flex align-items-center gap-3">
                                            <Avatar name={a.name || 'User'} index={index} />
                                            <div>
                                                <div className={styles.name}>{a.name || 'Unnamed'}</div>
                                                <div className={styles.sub}>
                                                    {a.phone} · applied {shortDate(a.affiliate.appliedAt)}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className={styles.code}>{a.affiliate.code || '--'}</td>
                                    <td>{a.affiliate.momoNumber || a.phone}</td>
                                    <td>
                                        <div>{a.stats.pending + a.stats.qualified + a.stats.paid} counted</div>
                                        <div className={styles.sub}>
                                            {a.stats.pending} waiting · {a.stats.qualified + a.stats.paid} qualified
                                            {a.stats.rejected ? ` · ${a.stats.rejected} rejected` : ''}
                                        </div>
                                    </td>
                                    <td>{money(a.stats.paidOut)}</td>
                                    <td className={styles.due}>
                                        {a.due.count ? money(a.due.kwacha) : '--'}
                                        {a.stats.owed > a.due.kwacha && (
                                            <div className={styles.sub}>{money(a.stats.owed - a.due.kwacha)} on hold</div>
                                        )}
                                    </td>
                                    <td>
                                        <StatusBadge status={cap(a.affiliate.status)} variant={BADGE_VARIANT[a.affiliate.status]} />
                                    </td>
                                    <td className="text-end position-relative">
                                        <ActionMenu ariaLabel="Affiliate actions" items={menuItems(a)} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>

            {detail && (
                <Modal onClose={() => setDetail(null)}>
                    <h3 className={`${styles.modalTitle} mb-1`}>{detail.affiliate.name || 'Affiliate'}</h3>
                    <div className={styles.sub}>{detail.affiliate.phone}</div>
                    <div className="mt-3">
                        <StatusBadge
                            status={cap(detail.affiliate.affiliate.status)}
                            variant={BADGE_VARIANT[detail.affiliate.affiliate.status]}
                        />
                    </div>

                    <ModalDivider />

                    <DetailRow label="Code" className="mb-3">
                        <span className={styles.code}>{detail.affiliate.affiliate.code || '--'}</span>
                    </DetailRow>
                    <DetailRow label="MoMo number" className="mb-3">
                        <span>{detail.affiliate.affiliate.momoNumber || detail.affiliate.phone}</span>
                    </DetailRow>
                    <DetailRow label="Earned" className="mb-3">
                        <span>{money(detail.stats.earned)}</span>
                    </DetailRow>
                    <DetailRow label="Paid out" className="mb-3">
                        <span>{money(detail.stats.paidOut)}</span>
                    </DetailRow>
                    <DetailRow label="Due now">
                        <span className={styles.due}>
                            {money(detail.due.kwacha)} ({detail.due.count} referral{detail.due.count === 1 ? '' : 's'})
                        </span>
                    </DetailRow>
                    {detail.affiliate.affiliate.note && (
                        <p className={`${styles.helpText} mt-3 mb-0`}>Note: {detail.affiliate.affiliate.note}</p>
                    )}

                    <ModalDivider />

                    <div className={`${styles.sectionTitle} mb-2`}>Referrals ({detail.referrals.length})</div>
                    <div className={styles.list}>
                        {detail.referrals.length === 0 && <p className={styles.sub}>No one has used this code yet.</p>}
                        {detail.referrals.map((r) => {
                            const u = r.referredUser || {};
                            return (
                                <div key={r.id} className={styles.listRow}>
                                    <div>
                                        <div className={styles.name}>{u.name || u.businessName || 'Deleted user'}</div>
                                        <div className={styles.sub}>
                                            {u.phone && !String(u.phone).startsWith('deleted:') ? `${u.phone} · ` : ''}
                                            joined {shortDate(r.createdAt)}
                                            {r.qualifiedBy?.kind === 'order' && ` · ${r.qualifiedBy.role} order`}
                                            {r.qualifiedBy?.kind === 'points-purchase' && ' · bought points'}
                                            {r.status === 'qualified' && ` · payable ${shortDate(r.payableAt)}`}
                                            {r.status === 'rejected' && ` · ${r.rejectedReason}`}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-3">
                                        <StatusBadge
                                            status={`${cap(r.status)}${r.bounty ? ` ${money(r.bounty)}` : ''}`}
                                            variant={REFERRAL_VARIANT[r.status]}
                                        />
                                        {(r.status === 'pending' || r.status === 'qualified') && (
                                            <button
                                                type="button"
                                                className={styles.linkButton}
                                                disabled={busy}
                                                onClick={() => rejectReferral(r)}
                                            >
                                                Reject
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {detail.payouts.length > 0 && (
                        <>
                            <ModalDivider />
                            <div className={`${styles.sectionTitle} mb-2`}>Payouts</div>
                            <div className={styles.list}>
                                {detail.payouts.map((p) => (
                                    <div key={p.id} className={styles.listRow}>
                                        <div>
                                            <div className={styles.name}>{money(p.total)}</div>
                                            <div className={styles.sub}>
                                                {shortDate(p.createdAt)} · to {p.momoNumber} · ref {p.reference}
                                                {p.paidBy?.name ? ` · by ${p.paidBy.name}` : ''}
                                            </div>
                                        </div>
                                        <span className={styles.sub}>
                                            {p.referrals.length} referral{p.referrals.length === 1 ? '' : 's'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </Modal>
            )}

            {approveFor && (
                <Modal onClose={() => setApproveFor(null)}>
                    <ModalTitle>Approve affiliate</ModalTitle>
                    <p className={`${styles.helpText} mt-2`}>
                        {approveFor.name} will be notified with their code and can start referring straight away.
                    </p>
                    <label htmlFor="aff-code" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Referral code
                    </label>
                    <input
                        id="aff-code"
                        className={`${styles.input} w-100`}
                        placeholder={approveFor.affiliate.code || 'Leave blank to generate one'}
                        value={customCode}
                        maxLength={12}
                        onChange={(event) => setCustomCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    />
                    <p className={`${styles.helpText} mt-2 mb-0`}>
                        4–12 letters or digits. Leave blank to generate one from their name.
                    </p>
                    <Button
                        className="w-100 mt-4"
                        disabled={busy || (customCode && customCode.length < 4)}
                        onClick={approve}
                    >
                        Approve
                    </Button>
                </Modal>
            )}

            {payoutFor && (
                <Modal onClose={() => setPayoutFor(null)} centered>
                    <ModalTitle>Record payout</ModalTitle>
                    <div className={`${styles.amount} mt-3`}>{money(payoutFor.due.kwacha)}</div>
                    <p className={styles.helpText}>
                        Send this by MoMo to <strong>{payoutFor.affiliate.momoNumber || payoutFor.phone}</strong> first,
                        then enter the transaction reference. It covers {payoutFor.due.count} referral
                        {payoutFor.due.count === 1 ? '' : 's'} past their hold.
                    </p>
                    <input
                        className={`${styles.input} w-100 mt-3`}
                        placeholder="MoMo transaction reference"
                        value={reference}
                        autoFocus
                        onChange={(event) => setReference(event.target.value)}
                    />
                    <Button className="w-100 mt-4" disabled={busy || !reference.trim()} onClick={recordPayout}>
                        Mark as paid
                    </Button>
                </Modal>
            )}

            {termsOpen && (
                <Modal onClose={() => setTermsOpen(false)}>
                    <ModalTitle>Affiliate terms</ModalTitle>

                    <label htmlFor="aff-bounty" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Bounty per qualified user (K)
                    </label>
                    <input
                        id="aff-bounty"
                        type="number"
                        min="0"
                        className={`${styles.input} w-100`}
                        value={termsForm.bounty}
                        onChange={(event) => setTermsForm({ ...termsForm, bounty: event.target.value })}
                    />
                    <p className={`${styles.helpText} mt-2 mb-0`}>
                        Only affects referrals that qualify from now on — already-earned bounties keep their amount.
                    </p>

                    <label htmlFor="aff-min" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Smallest qualifying order (K)
                    </label>
                    <input
                        id="aff-min"
                        type="number"
                        min="0"
                        className={`${styles.input} w-100`}
                        value={termsForm.minOrderTotal}
                        onChange={(event) => setTermsForm({ ...termsForm, minOrderTotal: event.target.value })}
                    />

                    <label htmlFor="aff-hold" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Hold before payout (days)
                    </label>
                    <input
                        id="aff-hold"
                        type="number"
                        min="0"
                        className={`${styles.input} w-100`}
                        value={termsForm.holdDays}
                        onChange={(event) => setTermsForm({ ...termsForm, holdDays: event.target.value })}
                    />

                    <label htmlFor="aff-window" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Days after signup a code can be added
                    </label>
                    <input
                        id="aff-window"
                        type="number"
                        min="0"
                        className={`${styles.input} w-100`}
                        value={termsForm.claimWindowDays}
                        onChange={(event) => setTermsForm({ ...termsForm, claimWindowDays: event.target.value })}
                    />

                    <label className={`${styles.checkboxRow} d-flex align-items-center gap-2 mt-4`}>
                        <input
                            type="checkbox"
                            checked={termsForm.enabled}
                            onChange={(event) => setTermsForm({ ...termsForm, enabled: event.target.checked })}
                        />
                        Accept new referral codes
                    </label>
                    <p className={`${styles.helpText} mt-2 mb-0`}>
                        Turning this off stops new codes being entered. Existing referrals still qualify and can still be paid.
                    </p>

                    <Button className="w-100 mt-4" disabled={!termsValid} onClick={saveTerms}>
                        Save changes
                    </Button>
                </Modal>
            )}
        </>
    );
};

export default Affiliates;
